import { FunctionComponent } from 'react'
import styled, { useTheme, css } from 'styled-components'
import { ellipsis } from 'polished'
import { NewstickerHorizontalPostProps } from '@widgets/Teaser/toi/types'
import { ViewportTypeValues } from '@contexts/viewport'
import { computeTeaserWidth } from '@widgets/Teaser/utils'
import SvgIcon from '@components/SvgIcon'
import { layout, desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import Blink from '@components/Blink'

type StyledNewstickerHorizontalPostProps = Pick<
  NewstickerHorizontalPostProps,
  'isFirstChild'
>

type StyledPostLineOuterWrapperProps = Pick<
  NewstickerHorizontalPostProps,
  'isFirstChild'
>

type StyledPostLineInnerWrapperProps = Pick<
  NewstickerHorizontalPostProps,
  'isLastChild'
>

const {
  desktop: { minWidth: minWidthDesktop },
  tablet: { minWidth: minWidthTablet },
} = layout

const StyledNewstickerHorizontalPost = styled(
  Blink
)<StyledNewstickerHorizontalPostProps>`
  ${({ isFirstChild }) => {
    const teaserWidthDesktopFlex6 = computeTeaserWidth({
      flexValue: '6',
      viewportType: ViewportTypeValues.DESKTOP,
    })

    const cornerPaddingDesktop = Math.round((1.6 / 100) * minWidthDesktop)
    const cornerPaddingTablet = Math.round((1.6 / 100) * minWidthTablet)

    const postWidthDesktop = `${
      (teaserWidthDesktopFlex6 - (cornerPaddingDesktop + 8)) / 3
    }px`

    const postWidthTablet = `${
      (teaserWidthDesktopFlex6 - (cornerPaddingTablet + 8)) / 3
    }px`

    return css`
      text-decoration: none;
      position: relative;
      display: flex;
      height: 85px;
      box-sizing: border-box;
      padding-top: 27px;

      ${desktopCSS(css`
        padding-left: ${isFirstChild
          ? `calc(${cornerPaddingDesktop + 8}px)`
          : '0'};
        width: ${isFirstChild
          ? `calc(${postWidthDesktop} + ${cornerPaddingDesktop + 8}px)`
          : postWidthDesktop};
      `)}

      ${tabletCSS(css`
        padding-left: ${isFirstChild
          ? `calc(${cornerPaddingTablet + 8}px)`
          : '0'};
        width: ${isFirstChild
          ? `calc(${postWidthTablet} + ${cornerPaddingTablet + 8}px)`
          : postWidthTablet};
      `)}

      ${mobileCSS(css`
        display: none;
      `)}
    `
  }}
`

const StyledPostDate = styled.div`
  width: 84px;
  ${({
    theme: {
      fonts: { sourceSansPro },
      colors: {
        grey: { grey_50 },
      },
    },
  }) => {
    return css`
      color: ${grey_50};
      font-family: ${sourceSansPro};
      font-size: 13px;
      line-height: 17px;
      text-transform: uppercase;
      padding-top: 2px;
    `
  }};
`

const StyledPostText = styled.div`
  ${({
    theme: {
      fonts: { sourceSansPro },
      colors: { white },
    },
  }) => {
    return css`
      color: ${white};
      font-family: ${sourceSansPro};
      font-weight: bold;
      font-size: 17px;
      line-height: 22px;
      height: 46px;
      flex: 1;
      box-sizing: border-box;
      padding-right: 20px;
      ${ellipsis('100%', 2)};
    `
  }};
`

const StyledPostLineOuterWrapper = styled.div<StyledPostLineOuterWrapperProps>`
  ${({ isFirstChild }) => {
    const cornerPaddingDesktop = Math.round((1.6 / 100) * minWidthDesktop)
    const cornerPaddingTablet = Math.round((1.6 / 100) * minWidthTablet)

    return css`
      display: flex;
      height: 27px;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      box-sizing: border-box;
      flex-direction: column;
      justify-content: flex-end;

      ${desktopCSS(css`
        padding-left: ${isFirstChild
          ? `calc(${cornerPaddingDesktop + 8}px)`
          : '0'};
      `)}

      ${tabletCSS(css`
        padding-left: ${isFirstChild
          ? `calc(${cornerPaddingTablet + 8}px)`
          : '0'};
      `)}

      ${mobileCSS(css`
        display: none;
      `)}
    `
  }}
`

const StyledPostLineInnerWrapper = styled.div<StyledPostLineInnerWrapperProps>`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  padding-right: ${({ isLastChild }) => `${isLastChild ? '0' : '10px;'}`};
  align-items: center;
  padding-bottom: 5px;
`

const StyledSvgIcon = styled(SvgIcon)`
  padding-right: 6px;
`

const StyledPostLine = styled.div`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
    },
  }) => {
    return css`
      flex: 1;
      height: 2px;
      background-color: ${brand_55};
    `
  }};
`

const NewstickerHorizontalPost: FunctionComponent<
  NewstickerHorizontalPostProps
> = ({ dateInfo, children, isFirstChild, isLastChild, clickableProps }) => {
  const theme = useTheme()
  return (
    <StyledNewstickerHorizontalPost
      {...(clickableProps as unknown as { href: string } & Record<
        string,
        unknown
      >)}
      isFirstChild={isFirstChild}>
      <StyledPostLineOuterWrapper isFirstChild={isFirstChild}>
        <StyledPostLineInnerWrapper isLastChild={isLastChild}>
          <StyledSvgIcon
            iconName="newsticker-clock"
            size={13}
            color={theme.colors.brandColors.brand_55}
          />
          <StyledPostLine />
        </StyledPostLineInnerWrapper>
      </StyledPostLineOuterWrapper>
      <StyledPostDate>{dateInfo.dateOrTime}</StyledPostDate>
      <StyledPostText>{children}</StyledPostText>
    </StyledNewstickerHorizontalPost>
  )
}

export default NewstickerHorizontalPost
